import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { SPIEntryForm, SPILoadingState, SPIScorecard } from "@/components/spi";
import type { SPIResult, ScorecardListItem } from "@/types/scorecard";
import { useCreateScorecard, useGetScorecard, useListScorecards, SCORECARD_ERROR_MESSAGE } from "@/resources/scorecard";
import { resolveProductImage } from "@/assets/products";
import { Card, CardContent } from "@/components/ui/card";
import { TruncatedText } from "@/components/ui/truncated-text";
import { ExternalLink, ArrowLeft } from "lucide-react";
import { AMAZON_ASIN_URL } from "@/constants/amazon";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";

type Phase = "entry" | "loading" | "scorecard";

type CompletedScorecardPayload = SPIResult & {
  id: number;
  asin: string;
  createdAt: string;
  completedAt: string | null;
};

export default function SignalPerformanceIndex() {
  const readOnly = useViewerReadOnly();
  const [searchParams, setSearchParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>("entry");
  const [currentAsin, setCurrentAsin] = useState("");
  const [pendingScorecardId, setPendingScorecardId] = useState<number | null>(null);
  const [selectedScorecardId, setSelectedScorecardId] = useState<number | null>(null);

  const createScorecard = useCreateScorecard();
  const qc = useQueryClient();

  const urlScorecardId = useMemo(() => {
    const raw = searchParams.get("scorecard");
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const resumeToastFiredRef = useRef<Set<number>>(new Set());
  const didMountRestoreRef = useRef(false);

  const { data: scorecardData, isLoading: scorecardLoading, isFetching: scorecardFetching } = useGetScorecard(
    phase === "scorecard" ? (selectedScorecardId ?? undefined) : undefined
  );

  const { data: resumeData, error: resumeError } = useGetScorecard(
    phase === "entry" && urlScorecardId !== null ? urlScorecardId : undefined
  );

  // AUT-366: this screen never passed `scorecardList`, so `SPIScorecard`'s report <Select> rendered
  // zero <SelectItem>s while `selectedReport` held a real id -- a value matching no item, which is
  // why the trigger drew blank and the open list was empty rather than showing a placeholder.
  // `currentAsin` is empty on a deep link (`/spi?scorecard=<id>`), so fall back to the ASIN the
  // loaded scorecard reports.
  const { data: scorecardList } = useListScorecards(currentAsin || scorecardData?.asin);

  const resetToEntry = useCallback(() => {
    setPhase("entry");
    setCurrentAsin("");
    setPendingScorecardId(null);
    setSelectedScorecardId(null);
    setSearchParams({}, { replace: true });
    try {
      sessionStorage.removeItem("spi:lastScorecardId");
    } catch {
      void 0;
    }
  }, [setSearchParams]);

  const handleSubmit = useCallback(
    (asin: string) => {
      if (createScorecard.isPending) return;
      setCurrentAsin(asin);
      createScorecard.mutate(
        { asin },
        {
          onSuccess: (data) => {
            setPendingScorecardId(data.id);
            setPhase("loading");
            setSearchParams({ scorecard: String(data.id) }, { replace: true });
            try {
              sessionStorage.setItem("spi:lastScorecardId", String(data.id));
            } catch {
              void 0;
            }
          },
          onError: () => {
            toast.error(SCORECARD_ERROR_MESSAGE);
            resetToEntry();
          },
        }
      );
    },
    [createScorecard, resetToEntry, setSearchParams]
  );

  const handleLoadingComplete = useCallback(
    (completed: SPIResult) => {
      const payload = completed as CompletedScorecardPayload;

      const newItem: ScorecardListItem = {
        id: payload.id,
        asin: payload.asin,
        status: "completed",
        overallScore: payload.overallScore,
        overallGrade: payload.overallGrade,
        createdAt: payload.createdAt,
        completedAt: payload.completedAt ?? undefined,
      };

      qc.setQueryData(
        ["scorecards", "list", payload.asin],
        (prev: ScorecardListItem[] | undefined) => {
          if (!prev) return [newItem];
          if (prev.some((x) => x.id === newItem.id)) return prev;
          return [newItem, ...prev];
        }
      );

      qc.invalidateQueries({ queryKey: ["scorecards", "list", payload.asin] });

      setSelectedScorecardId(payload.id);
      setPendingScorecardId(null);
      setPhase("scorecard");
      setSearchParams({ scorecard: String(payload.id) }, { replace: true });
      try {
        sessionStorage.setItem("spi:lastScorecardId", String(payload.id));
      } catch {
        void 0;
      }
    },
    [qc, setSearchParams]
  );

  const handleLoadingError = useCallback(() => {
    toast.error(SCORECARD_ERROR_MESSAGE);
    resetToEntry();
  }, [resetToEntry]);

  const handleBack = () => {
    setPhase("entry");
    setCurrentAsin("");
    setPendingScorecardId(null);
    setSelectedScorecardId(null);
    setSearchParams({}, { replace: true });
    try {
      sessionStorage.removeItem("spi:lastScorecardId");
    } catch {
      void 0;
    }
  };

  useEffect(() => {
    if (didMountRestoreRef.current) return;
    didMountRestoreRef.current = true;
    if (searchParams.get("scorecard") !== null) return;
    try {
      const raw = sessionStorage.getItem("spi:lastScorecardId");
      if (raw === null) return;
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) {
        setSearchParams({ scorecard: String(parsed) }, { replace: true });
      }
    } catch {
      void 0;
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (phase !== "entry") return;
    if (urlScorecardId === null) return;

    if (resumeError) {
      toast.error("Amazon AI Score not found");
      resetToEntry();
      return;
    }

    if (!resumeData) return;

    if (resumeData.status === "completed") {
      setCurrentAsin(resumeData.asin);
      setSelectedScorecardId(resumeData.id);
      setPhase("scorecard");
      return;
    }

    if (resumeData.status === "failed") {
      toast.error(SCORECARD_ERROR_MESSAGE);
      resetToEntry();
      return;
    }

    setCurrentAsin(resumeData.asin);
    setPendingScorecardId(resumeData.id);
    setPhase("loading");

    if (!resumeToastFiredRef.current.has(resumeData.id)) {
      resumeToastFiredRef.current.add(resumeData.id);
      toast.info(`Resuming Amazon AI Score generation for ASIN ${resumeData.asin}...`);
    }
  }, [phase, urlScorecardId, resumeData, resumeError, resetToEntry]);

  const completedData =
    scorecardData && scorecardData.status === "completed"
      ? (scorecardData as CompletedScorecardPayload)
      : null;

  const [imgSrc, setImgSrc] = useState<string>("/placeholder.svg");

  useEffect(() => {
    if (!completedData) return;
    const fallback = resolveProductImage("/placeholder.svg", completedData.asin, completedData.productBrand);
    setImgSrc(completedData.mainImageUrl ?? fallback);
  }, [completedData]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Amazon AI Score</h1>
        <p className="text-muted-foreground text-sm">COSMO-aligned audit for Amazon listings</p>
      </div>

      {phase === "entry" && (
        readOnly ? (
          <p className="text-muted-foreground text-sm max-w-md">
            View-only accounts cannot generate an Amazon AI Score here. You can still open a completed Amazon AI Score from a product’s Amazon AI Score tab when available.
          </p>
        ) : (
          <SPIEntryForm onSubmit={handleSubmit} isSubmitting={createScorecard.isPending} />
        )
      )}

      {phase === "loading" && pendingScorecardId !== null && (
        <SPILoadingState
          scorecardId={pendingScorecardId}
          onComplete={handleLoadingComplete}
          onError={handleLoadingError}
        />
      )}

      {phase === "scorecard" && (
        <>
          {(scorecardLoading || (scorecardFetching && !scorecardData)) && (
            <div className="flex items-center justify-center min-h-[40vh]" />
          )}

          {!scorecardLoading && !completedData && scorecardData && (
            <div className="flex items-center justify-center min-h-[40vh]">
              <p className="text-muted-foreground text-sm">Amazon AI Score is still processing.</p>
            </div>
          )}

          {completedData && (
            <div className="space-y-6 mx-auto">
              {!readOnly ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Run another Amazon AI Score
                </button>
              ) : null}

              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  {/* AUT-279 (reopened): the client rejected the grey bars, so the box is bg-white. */}
                  <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0 border-2 border-card">
                    <img
                      src={imgSrc}
                      alt={completedData.productTitle}
                      className="w-full h-full object-contain"
                      onError={() =>
                        setImgSrc(
                          resolveProductImage("/placeholder.svg", completedData.asin, completedData.productBrand)
                        )
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground font-medium">{completedData.productBrand}</p>
                    <p className="text-sm font-semibold text-foreground leading-snug">
                      <TruncatedText lines={2}>{completedData.productTitle}</TruncatedText>
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground font-mono">{completedData.asin}</span>
                      <span className="text-xs text-muted-foreground">|</span>
                      <a
                        href={`${AMAZON_ASIN_URL}/${completedData.asin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                      >
                        Amazon US
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <SPIScorecard
                result={completedData}
                onBack={handleBack}
                hideBackButton
                hideProductInfo
                asin={currentAsin || completedData.asin}
                selectedScorecardId={selectedScorecardId ?? undefined}
                onSelectScorecard={(id) => setSelectedScorecardId(id)}
                scorecardList={scorecardList}
                // The design labels the latest report "Current" on this screen; the product tab
                // keeps dates on every row.
                newestOptionLabel="Current"
                readOnly={readOnly}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
